import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { CircleStop } from 'lucide-react';
import type { Point } from '@/types/common';
import { useAppStore } from '@/stores/appStore';

/**
 * Constrains a line to horizontal or vertical (90-degree angles)
 * Used when Shift key is held during strip drawing
 */
function constrainToHV(lastPoint: Point, currentPoint: Point): Point {
  const dx = Math.abs(currentPoint.x - lastPoint.x);
  const dy = Math.abs(currentPoint.y - lastPoint.y);

  if (dx > dy) {
    // Constrain to horizontal
    return { x: currentPoint.x, y: lastPoint.y };
  } else {
    // Constrain to vertical
    return { x: lastPoint.x, y: currentPoint.y };
  }
}

/**
 * Calculate total length of a polyline path in pixels
 */
function calculatePolylineLength(points: Point[]): number {
  if (points.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1].x - points[i].x;
    const dy = points[i + 1].y - points[i].y;
    total += Math.sqrt(dx * dx + dy * dy);
  }
  return total;
}

interface StripDrawingOverlayProps {
  /** Floor plan ID for creating the strip */
  floorPlanId: string;
  /** Container reference for coordinate calculations */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Whether the LED strip drawing tool is active in the viewer toolbar */
  isActive: boolean;
  /** Callback when drawing is complete */
  onComplete?: () => void;
  /** Callback when drawing is cancelled or another interaction should take over */
  onCancel?: () => void;
  /** Current zoom level */
  zoom: number;
  /** Current pan position */
  position: { x: number; y: number };
  /** Image dimensions */
  imageSize: { width: number; height: number };
}

/**
 * StripDrawingOverlay handles the click-to-add-points interaction for creating LED strip fixtures.
 * Similar to cable path drawing - click to add vertices, context menu to finish.
 */
export function StripDrawingOverlay({
  floorPlanId,
  containerRef,
  isActive,
  onComplete,
  onCancel,
  zoom,
  position,
  imageSize,
}: StripDrawingOverlayProps) {
  // State for polyline drawing
  const [points, setPoints] = useState<Point[]>([]);
  const [cursorPos, setCursorPos] = useState<Point | null>(null);
  const [isShiftHeld, setIsShiftHeld] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(
    null,
  );

  // Get floor plan context for creating fixtures
  const { data } = useFloorPlanContext();
  const clearAllSelections = useAppStore(state => state.clearAllSelections);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);

  const isDrawing = points.length > 0;

  // Convert screen coordinates to floor plan coordinates
  const screenToFloorPlanCoords = useCallback(
    (clientX: number, clientY: number): Point | null => {
      if (!containerRef.current) return null;

      const rect = containerRef.current.getBoundingClientRect();
      const x = (clientX - rect.left - position.x) / zoom;
      const y = (clientY - rect.top - position.y) / zoom;

      return { x, y };
    },
    [containerRef, position, zoom],
  );

  // Get the last point for shift-constraint calculations
  const getLastPoint = useCallback((): Point | null => {
    if (points.length > 0) {
      return points[points.length - 1];
    }
    return null;
  }, [points]);

  // Apply shift-key constraint for horizontal/vertical lines
  const applyShiftConstraint = useCallback(
    (point: Point): Point => {
      const lastPoint = getLastPoint();
      if (!lastPoint) return point;
      return constrainToHV(lastPoint, point);
    },
    [getLastPoint],
  );

  // Handle mouse move - update preview line
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isActive) return;

      setIsShiftHeld(e.shiftKey);

      let point = screenToFloorPlanCoords(e.clientX, e.clientY);
      if (!point) return;

      // Apply shift constraint when drawing
      if (isDrawing && e.shiftKey) {
        point = applyShiftConstraint(point);
      }

      setCursorPos(point);
    },
    [isActive, isDrawing, screenToFloorPlanCoords, applyShiftConstraint],
  );

  // Handle click to add point
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!isActive) return;

      // Don't process clicks on context menu
      const target = e.target as HTMLElement;
      if (target.closest('[data-strip-context-menu]') || target.closest('button')) {
        return;
      }

      // Check if clicking on interactive elements
      const isInteractiveElement =
        target.closest('[data-fixture-id]') ||
        target.closest('[data-control-id]') ||
        target.closest('[data-panel-id]') ||
        target.closest('[data-annotation-id]');

      if (isInteractiveElement && !isDrawing) {
        // Exit strip mode and let the click through
        onCancel?.();
        return;
      }

      // If context menu is visible and clicking on canvas, dismiss it and continue adding point
      if (showContextMenu) {
        setShowContextMenu(false);
        setContextMenuPosition(null);
        // Continue to add the next point - don't return early
      }

      let point = screenToFloorPlanCoords(e.clientX, e.clientY);
      if (!point) return;

      e.preventDefault();
      e.stopPropagation();

      // Apply shift constraint for horizontal/vertical lines
      if (isDrawing && e.shiftKey) {
        point = applyShiftConstraint(point);
      }

      logger.log('StripDrawingOverlay - Adding point:', point);

      // Add point to the array
      setPoints(prev => [...prev, point!]);

      // Show context menu after adding a point (except the first one)
      if (points.length > 0) {
        setShowContextMenu(true);
        setContextMenuPosition({ x: e.clientX, y: e.clientY });
      }
    },
    [
      isActive,
      isDrawing,
      showContextMenu,
      points.length,
      screenToFloorPlanCoords,
      applyShiftConstraint,
      onCancel,
    ],
  );

  // Handle "End" button - finish the strip
  const handleEnd = useCallback(async () => {
    if (points.length < 2 || isProcessing) {
      if (points.length < 2) {
        toast.warning('Strip needs at least 2 points', {
          description: 'Click to add more points before ending',
        });
      }
      return;
    }

    setIsProcessing(true);
    setShowContextMenu(false);

    try {
      const totalLength = calculatePolylineLength(points);

      logger.log('Creating strip with points:', points, 'length:', totalLength);

      // Create the strip fixture with all points
      const stripData = {
        floor_plan_id: floorPlanId,
        // Start point (first point)
        x_position: points[0].x,
        y_position: points[0].y,
        // End point (last point)
        end_x_position: points[points.length - 1].x,
        end_y_position: points[points.length - 1].y,
        // All points for polyline rendering
        strip_points: points,
        fixture_form_factor: 'linear' as const,
        fixture_type: 'led_strip',
        wattage: 10, // Default wattage
        confidence_score: 1.0, // User-created
        user_verified: true,
        strip_length_mm: totalLength, // Store pixel distance for now
        category: 'LED Strip',
        subcategory: 'Linear',
      };

      if (data.createFixture) {
        await data.createFixture(stripData, { showSuccessToast: false });
        toast.success('Strip created', {
          description: `LED strip with ${points.length} points (${Math.round(totalLength)}px)`,
        });

        // Leave the canvas in neutral select mode after finishing the strip.
        // Auto-opening the fixture drawer intercepts the next toolbar click.
      }

      // Reset state
      setPoints([]);
      setCursorPos(null);
      setContextMenuPosition(null);
      clearAllSelections();
      setActiveDrawer(null);

      // Notify parent
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      logger.error('Failed to create strip:', error);
      toast.error('Failed to create strip');
    } finally {
      setIsProcessing(false);
    }
  }, [points, isProcessing, floorPlanId, data, clearAllSelections, setActiveDrawer, onComplete]);

  // Handle ESC key to cancel
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isActive) return;

      if (e.key === 'Escape') {
        if (showContextMenu) {
          setShowContextMenu(false);
          setContextMenuPosition(null);
        } else if (isDrawing) {
          setPoints([]);
          setCursorPos(null);
          toast.info('Strip drawing cancelled');
        } else {
          onCancel?.();
        }
      }
    },
    [isActive, isDrawing, showContextMenu, onCancel],
  );

  // Add event listeners when active
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, containerRef, handleMouseMove, handleClick, handleKeyDown]);

  // Build preview path for SVG
  const previewPath = useMemo(() => {
    const allPoints = [...points];

    // Add cursor position for the live preview line. Keep this visible even
    // while the End button is open so multi-point strips show the next segment
    // before the user commits or adds another vertex.
    if (isDrawing && cursorPos) {
      allPoints.push(cursorPos);
    }

    if (allPoints.length < 2) return '';
    const [first, ...rest] = allPoints;
    return `M ${first.x} ${first.y} ${rest.map(p => `L ${p.x} ${p.y}`).join(' ')}`;
  }, [points, cursorPos, isDrawing]);

  // Calculate preview length
  const previewLength = useMemo(() => {
    const allPoints = [...points];
    if (isDrawing && cursorPos) {
      allPoints.push(cursorPos);
    }
    return Math.round(calculatePolylineLength(allPoints));
  }, [points, cursorPos, isDrawing]);

  // Don't render if not active
  if (!isActive) return null;

  return (
    <>
      {/* SVG overlay for drawing preview - transformed to match viewport */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          zIndex: 50,
        }}
      >
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'visible',
          }}
        >
          {/* Preview path line */}
          {previewPath && (
            <path
              d={previewPath}
              fill="none"
              stroke="#fbbf24"
              strokeWidth={4 / zoom}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={`${12 / zoom} ${6 / zoom}`}
              opacity={0.9}
            />
          )}

          {/* Vertex markers */}
          {points.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r={6 / zoom}
              fill={index === 0 ? '#fbbf24' : 'white'}
              stroke="#fbbf24"
              strokeWidth={2 / zoom}
            />
          ))}

          {/* Cursor indicator (when not showing context menu) */}
          {cursorPos && isDrawing && (
            <circle
              cx={cursorPos.x}
              cy={cursorPos.y}
              r={6 / zoom}
              fill="white"
              fillOpacity={0.5}
              stroke="#fbbf24"
              strokeWidth={2 / zoom}
            />
          )}

          {/* Live length label (when drawing and not showing context menu) */}
          {isDrawing && cursorPos && previewLength > 0 && (
            <g>
              <rect
                x={cursorPos.x + 15 / zoom}
                y={cursorPos.y - 25 / zoom}
                width={70 / zoom}
                height={24 / zoom}
                rx={4 / zoom}
                fill="white"
                fillOpacity={0.95}
                stroke="#fbbf24"
                strokeWidth={1 / zoom}
              />
              <text
                x={cursorPos.x + 50 / zoom}
                y={cursorPos.y - 8 / zoom}
                textAnchor="middle"
                fill="#b45309"
                fontSize={12 / zoom}
                fontWeight={600}
                fontFamily="system-ui, sans-serif"
              >
                {previewLength}px
              </text>
            </g>
          )}
        </svg>
      </div>

      {/* Context menu for ending the strip */}
      {showContextMenu && contextMenuPosition && (
        <div
          style={{
            position: 'fixed',
            left: contextMenuPosition.x + 12,
            top: contextMenuPosition.y - 44,
            zIndex: 1001,
          }}
          className="flex gap-1 bg-white rounded-lg shadow-lg border border-border p-1 animate-in fade-in-0 zoom-in-95 duration-100"
          data-strip-context-menu="true"
          onClick={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
        >
          <Button
            size="sm"
            variant="default"
            onClick={handleEnd}
            disabled={isProcessing || points.length < 2}
            className="h-8 px-3 text-xs font-medium bg-amber-500 hover:bg-amber-600"
            title="End strip here"
          >
            <CircleStop className="h-3 w-3 mr-1" />
            End
          </Button>
        </div>
      )}

      {/* Instructions tooltip */}
      {!isDrawing && (
        <div
          style={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}
          className="bg-black/80 text-white px-4 py-3 rounded-lg text-sm max-w-md text-center"
        >
          <div className="font-medium mb-1">LED Strip Tool</div>
          <div className="text-white/80">
            Click to start drawing. Click adds turning points.
            <br />
            Hold <kbd className="bg-white/20 px-1 rounded">Shift</kbd> for straight lines.
            <br />
            Click <span className="text-amber-400">End</span> to finish the strip.
          </div>
        </div>
      )}

      {/* Drawing mode indicator */}
      {isDrawing && !showContextMenu && (
        <div
          style={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}
          className="bg-black/60 text-white px-3 py-2 rounded-lg text-xs"
        >
          {points.length} {points.length === 1 ? 'point' : 'points'} •
          {isShiftHeld ? ' Straight line mode' : ' Click to add vertex'} • Press{' '}
          <kbd className="bg-white/20 px-1 rounded">Esc</kbd> to cancel
        </div>
      )}
    </>
  );
}

export default StripDrawingOverlay;
